import { Link, useLocation } from "react-router-dom";
import { forwardRef } from "react";
import { getLangFromPath, langPrefix } from "@/i18n/LanguageContext";

/**
 * Drop-in replacement for react-router's <Link> that auto-prefixes
 * absolute internal paths with the active language tree.
 *
 * English is the canonical site root, so it has NO prefix. Arabic lives
 * under `/ar`.
 *
 *   <LocalizedLink to="/tools"> on EN  → /tools
 *   <LocalizedLink to="/tools"> on AR  → /ar/tools
 *   <LocalizedLink to="/">      on EN  → /
 *   <LocalizedLink to="/">      on AR  → /ar
 *
 * Already-localised paths (anything starting with `/ar`) are passed through
 * untouched so we never double-prefix.
 *
 * Relative paths and non-string `to` values are passed through unchanged.
 */
const LocalizedLink = forwardRef(function LocalizedLink({ to, ...rest }, ref) {
  const { pathname } = useLocation();
  const lang = getLangFromPath(pathname);
  const prefix = langPrefix(lang); // "" or "/ar"

  let dest = to;
  if (typeof to === "string" && to.startsWith("/")) {
    if (to === "/ar" || to.startsWith("/ar/")) {
      // Caller passed an already-localised AR path — keep as-is.
      dest = to;
    } else if (to === "/") {
      dest = prefix || "/";
    } else {
      dest = `${prefix}${to}`;
    }
  }

  return <Link ref={ref} to={dest} {...rest} />;
});

export default LocalizedLink;
